import {
  ArrowLeftOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Popconfirm,
  Row,
  Skeleton,
  Spin,
  Typography,
} from "antd";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fetchAviationFormStep,
  updateAviationFormStep,
} from "../../../store/applicationsSearch/applicationsSearchThunks";
import ApplicationHeaderBar from "../shared/ApplicationHeaderBar";

const { Title } = Typography;

const EditSectionPage = ({ title, step, schema, children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const { pathname } = useLocation();
  const isRenewal = pathname.startsWith("/renewals/");

  const data = useSelector(
    (state) => state.applicationsSearch.editApplication.data
  );
  const isLoading = useSelector(
    (state) => state.applicationsSearch.editApplication.isLoading
  );
  const isSaving = useSelector(
    (state) => state.applicationsSearch.editApplication.isSaving
  );

  const formMethods = useForm({
    mode: "onChange",
    defaultValues: data,
    values: data,
    shouldFocusError: false,
    resetOptions: {
      keepDirty: false,
      keepDirtyValues: false,
      keepErrors: true,
    },
    resolver: yupResolver(schema),
  });

  const {
    getValues,
    reset,
    formState: { isDirty, isValid },
  } = formMethods;

  useEffect(() => {
    if (applicationId) {
      window.scrollTo(0, 0);
      dispatch(fetchAviationFormStep({ id: applicationId, step: step }));
    }
  }, [dispatch, applicationId]);

  useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data]);

  const headerActions = [
    isDirty ? (
      <Popconfirm
        title={"Unsaved changes"}
        description={
          "You have unsaved changes. Are you sure you want to go back?"
        }
        onConfirm={() => navigate(-1)}
        okText="Yes"
        cancelText="No"
      >
        <Button key="previous" size="small" icon={<ArrowLeftOutlined />}>
          Go Back
        </Button>
      </Popconfirm>
    ) : (
      <Button
        key="previous"
        size="small"
        onClick={() => navigate(-1)}
        icon={<ArrowLeftOutlined />}
      >
        Go Back
      </Button>
    ),
    <Button
      size="small"
      disabled={!isDirty}
      onClick={() => {
        reset(data);
      }}
      icon={<ReloadOutlined />}
    >
      Restore
    </Button>,
    <Button
      key="save"
      size="small"
      type="primary"
      icon={<SaveOutlined />}
      disabled={!isDirty || !isValid}
      onClick={() => {
        const data = getValues();
        dispatch(
          updateAviationFormStep({ id: applicationId, step: step, data: data })
        );
      }}
    >
      Save
    </Button>,
  ];

  return !isLoading && !data ? (
    <Alert
      type="error"
      showIcon
      message="Error: Something went wrong while loading this application."
      className="default-margin"
    />
  ) : (
    <Card className="body-card-content-no-min-height">
      <Skeleton active loading={isLoading}>
        <Spin spinning={isSaving}>
          <ApplicationHeaderBar
            data={data}
            actions={headerActions}
            isRenewal={isRenewal}
          />
          <Divider style={{ marginTop: 8, marginBottom: 8 }} />
          <Row>
            <Col span={24} style={{ marginBottom: 8 }}>
              <Title level={5} style={{ margin: 0 }}>
                {title}
              </Title>
            </Col>
          </Row>

          {/* RHF context + AntD Form wrapper (size="small") */}
          <FormProvider {...formMethods}>
            <Form layout="vertical" size="small" autoComplete="off">
              <Row>
                <Col span={24}>{children}</Col>
              </Row>
            </Form>
          </FormProvider>
        </Spin>
      </Skeleton>
    </Card>
  );
};

export default EditSectionPage;
